import "cesium/Build/Cesium/Widgets/widgets.css";
import "./style.css";
import { Cartesian2, ScreenSpaceEventHandler, ScreenSpaceEventType } from "cesium";
import { createViewer, flyToLonLat } from "./cesium/viewer";
import { LayerManager, type AssetRef } from "./cesium/layers";
import { api, type Asset } from "./api";
import { renderTree, refreshTreeItemReview, overallStatus, dimState, expectedDims } from "./ui/layerTree";
import { ReviewBar } from "./ui/reviewBar";
import { mountHelp } from "./ui/help";
import { mountSunTool } from "./ui/sunTool";
import { mountFloodPanel } from "./ui/floodPanel";
import { mountViewTools } from "./ui/viewTools";
import { openIngestPanel } from "./ui/ingestPanel";
import { activityBar } from "./ui/activityBar";
import { JobsWidget } from "./ui/jobsWidget";
import { MarkupManager } from "./cesium/markup";
import { mountMarkupTools } from "./ui/markupPanel";
import { renderUnlinkedTray, type FlatItem } from "./ui/unlinkedTray";
import { ensureAuth } from "./ui/login";
import { toast } from "./ui/toast";
import type { RegisterNode, ReviewState } from "./api";
import { $connected, $backendVersion, $activeProjectId, $selectedItemName, $selectedItemId, $user } from "./store";

// Collapsible sidebar sections.
document.querySelectorAll(".section-head").forEach((head) => {
  head.addEventListener("click", () => {
    const section = head.parentElement!;
    const collapsed = section.classList.toggle("collapsed");
    head.querySelector(".caret")!.textContent = collapsed ? "▸" : "▾";
  });
});

const container = document.getElementById("cesium-container");
if (!container) throw new Error("missing #cesium-container");

const viewer = createViewer(container);
const layers = new LayerManager(viewer);
layers.onLoadingStart = () => activityBar.incLoading();
layers.onLoadingEnd = () => activityBar.decLoading();
// Expose for debugging in the browser console during development.
Object.assign(window as object, { viewer, layers });

const markup = new MarkupManager(viewer);
let jobsWidget: JobsWidget | null = null;
let markupPanel: { refresh: () => Promise<void> } | null = null;

const topbar = document.getElementById("topbar");
const markupListEl = document.getElementById("markup-list");
const floodPanelEl = document.getElementById("flood-panel");
const floodPanel = floodPanelEl ? mountFloodPanel(floodPanelEl, layers) : null;

// Role gating: only Viewport (admin) may create projects, ingest, or auto-match.
// The client login is review-only. Defaults to hidden until the role resolves.
let isAdmin = false;
const adminOnlyEls: HTMLElement[] = [];
function applyAdminGating(): void {
  for (const el of adminOnlyEls) el.style.display = isAdmin ? "" : "none";
}

if (topbar) {
  // Project switcher + creation (multiple deliveries / milestones = multiple projects).
  const projSelect = document.createElement("select");
  projSelect.className = "proj-select";
  projSelect.title = "Switch project (one per delivery or milestone)";
  projSelect.addEventListener("change", async () => {
    if (projSelect.value === "__new__") {
      await createProjectFlow();
      return;
    }
    const pid = Number(projSelect.value);
    localStorage.setItem("geaar.activeProject", String(pid));
    $activeProjectId.set(pid);
    await reloadProject(pid, true);
    await markupPanel?.refresh();
  });
  topbar.insertBefore(projSelect, topbar.querySelector(".status"));

  async function refreshProjectList(): Promise<void> {
    const projects = await api.listProjects();
    projSelect.innerHTML = "";
    for (const p of projects) {
      const o = document.createElement("option");
      o.value = String(p.id);
      o.textContent = p.name;
      projSelect.append(o);
    }
    if (isAdmin) {
      const add = document.createElement("option");
      add.value = "__new__";
      add.textContent = "＋ New project…";
      projSelect.append(add);
    }
    const active = $activeProjectId.get();
    if (active != null) projSelect.value = String(active);
  }
  $activeProjectId.subscribe(() => void refreshProjectList());

  async function createProjectFlow(): Promise<void> {
    const name = window.prompt("Project name (e.g. Wyloo North Beta):");
    if (!name) {
      void refreshProjectList();
      return;
    }
    const project = await api.createProject({ name });
    const reg = window.prompt(
      "Path to the Data Register xlsx (optional - leave blank to review without one; " +
        "you can adopt scanned files as register items instead):",
    );
    if (reg?.trim()) {
      try {
        const res = await api.importRegister(project.id, reg.trim());
        toast(`Register imported: ${String(res.items ?? "")} items`, "ok");
      } catch (err) {
        toast(`Register import failed: ${(err as Error).message}`, "warn");
      }
    }
    localStorage.setItem("geaar.activeProject", String(project.id));
    $activeProjectId.set(project.id);
    await reloadProject(project.id, true);
    toast(`Project "${project.name}" ready - use Ingest data to load files`, "ok");
  }

  const ingestBtn = document.createElement("button");
  ingestBtn.className = "tool-btn ingest-open";
  ingestBtn.textContent = "Ingest data";
  ingestBtn.addEventListener("click", () => {
    const pid = $activeProjectId.get();
    if (pid != null) openIngestPanel(pid, (jobIds) => jobsWidget?.watch(pid, jobIds));
  });
  topbar.append(ingestBtn);
  adminOnlyEls.push(ingestBtn);

  const matchBtn = document.createElement("button");
  matchBtn.className = "tool-btn";
  matchBtn.textContent = "Auto-match";
  matchBtn.title = "Link unlinked assets to register items using a GEAAR layer-hierarchy register xlsx";
  matchBtn.addEventListener("click", async () => {
    const pid = $activeProjectId.get();
    if (pid == null) return;
    const path = window.prompt(
      "Path to the GEAAR layer-hierarchy register (.xlsx):",
      "D:\\Claude Projects\\GEAAR Review Station\\data\\Wyloo GEAAR Data Register.xlsx",
    );
    if (!path) return;
    matchBtn.disabled = true;
    matchBtn.textContent = "Matching…";
    try {
      const r = await api.autoMatch(pid, path);
      toast(
        `Auto-matched ${r.linked_existing + r.created} assets (${r.created} new items, ${r.unmatched} unmatched)`,
        "ok",
        6000,
      );
      await reloadProject(pid);
    } catch (err) {
      toast(`Auto-match failed: ${(err as Error).message}`, "warn");
    } finally {
      matchBtn.disabled = false;
      matchBtn.textContent = "Auto-match";
    }
  });
  topbar.append(matchBtn);
  adminOnlyEls.push(matchBtn);
  applyAdminGating();

  const exportBtn = document.createElement("button");
  exportBtn.className = "tool-btn";
  exportBtn.textContent = "Export";
  exportBtn.title = "Export the reviewed register (xlsx) + sign-off report (PDF)";
  exportBtn.addEventListener("click", async () => {
    const pid = $activeProjectId.get();
    if (pid == null) return;
    exportBtn.disabled = true;
    exportBtn.textContent = "Exporting…";
    try {
      const report = await api.exportReport(pid);
      let regMsg = "";
      try {
        const reg = await api.exportRegister(pid);
        regMsg = ` · register: ${reg.file}`;
      } catch {
        regMsg = " · no source register to update (report only)";
      }
      toast(`Exported${regMsg}`, "ok", 5000);
      window.open(report.pdf_url ?? report.html_url, "_blank");
    } catch (err) {
      toast(`Export failed: ${(err as Error).message}`, "warn");
    } finally {
      exportBtn.disabled = false;
      exportBtn.textContent = "Export";
    }
  });
  topbar.append(exportBtn);

  const logoutBtn = document.createElement("button");
  logoutBtn.className = "tool-btn";
  logoutBtn.textContent = "Sign out";
  logoutBtn.title = "Sign out";
  // Always sit at the far right of the top bar regardless of DOM order.
  logoutBtn.style.order = "999";
  logoutBtn.style.marginLeft = "auto";
  logoutBtn.addEventListener("click", async () => {
    await api.logout();
    location.reload();
  });
  topbar.append(logoutBtn);

  jobsWidget = new JobsWidget(topbar);
  jobsWidget.onAssetsLanded = () => {
    const pid = $activeProjectId.get();
    if (pid != null) void reloadProject(pid);
  };

  if (markupListEl) {
    markupPanel = mountMarkupTools(
      topbar,
      markupListEl,
      markup,
      () => viewer.canvas as HTMLCanvasElement,
      () => void reviewBar.refreshComments(), // resolving/editing in the log reflects in the bar
      (itemId) => itemNames.get(itemId) ?? null,
      (itemId) => {
        const n = itemNodes.get(itemId);
        if (n) selectAndFly(n, true); // open the layer's review bar + fly to it
      },
    );
  }

  mountHelp(topbar);
  mountSunTool(topbar, viewer);

  // (Selected-layer name lives in the bottom review bar now, not the top bar.)

  // Plan view legend (shown only in plan mode).
  const legend = document.createElement("div");
  legend.className = "plan-legend";
  legend.innerHTML =
    '<div class="pl-title">Sign-off plan</div>' +
    [
      ["#22C55E", "Signed off"],
      ["#ff4d4d", "Needs work"],
      ["#e0a020", "To review"],
      ["#5a626b", "Excluded"],
    ]
      .map(([c, t]) => `<div class="pl-row"><span class="pl-sw" style="background:${c}"></span>${t}</div>`)
      .join("");
  document.body.append(legend);

  const onPlanToggle = (on: boolean) => {
    planActive = on;
    layers.setPlanColoring(on ? planColorFor : null);
    legend.classList.toggle("show", on);
    toast(on ? "Plan view: layers coloured by sign-off" : "Plan view off", "info");
  };

  mountViewTools(topbar, layers, viewer, async (dim, on) => {
    // Bulk toggle scoped to the CURRENT project's assets only (terrain excluded -
    // it has its own toggle and only one can be active).
    let targets = currentAssets.filter((a) => a.dimension === dim && a.kind !== "terrain");
    if (on) {
      // Don't surface struck-through (excluded) layers when showing everything.
      const excluded = new Set<number>();
      for (const node of itemNodes.values()) {
        if (dimState(node, dim) === "excluded") {
          for (const a of node.assets) if (a.dimension === dim) excluded.add(a.id);
        }
      }
      targets = targets.filter((a) => !excluded.has(a.id));
    }
    activityBar.incLoading();
    try {
      // Chunked so 100+ glTF fetches don't stampede the browser.
      for (let i = 0; i < targets.length; i += 8) {
        await Promise.all(
          targets.slice(i, i + 8).map((a) => layers.setVisible(toAssetRef(a), on)),
        );
      }
      toast(`${on ? "Showing" : "Hid"} ${targets.length} ${dim} layer${targets.length === 1 ? "" : "s"}`, "info");
    } finally {
      activityBar.decLoading();
    }
  }, onPlanToggle);
}

const statusEl = document.getElementById("status");
function setStatus(text: string, cls: "ok" | "err" | "") {
  if (!statusEl) return;
  statusEl.textContent = text;
  statusEl.className = `status ${cls}`.trim();
}

const treeEl = document.getElementById("layer-tree");
const unlinkedEl = document.getElementById("unlinked-tray");

// Flatten the register tree into pickable "Category > Item" entries.
function flattenItems(roots: RegisterNode[]): FlatItem[] {
  const out: FlatItem[] = [];
  const walk = (n: RegisterNode, path: string) => {
    const label = path ? `${path} > ${n.layer_name ?? ""}` : n.layer_name ?? "";
    if (n.node_type === "item") out.push({ id: n.id, label });
    n.children.forEach((c) => walk(c, label));
  };
  roots.forEach((r) => walk(r, ""));
  return out;
}

function toAssetRef(a: Asset): AssetRef {
  return {
    id: a.id,
    kind: a.kind,
    dimension: a.dimension,
    web_path: a.web_path,
    placement_matrix: a.placement_matrix,
    lon: a.lon,
    lat: a.lat,
    height: a.height,
    bbox: a.bbox,
    style: (a.style ?? null) as AssetRef["style"],
    radius: a.radius ?? null,
    icon: (a.icon ?? null) as AssetRef["icon"],
  };
}

// item id -> layer name (for markup layer chips); refreshed on every project reload.
const itemNames = new Map<number, string>();
// id -> node and an ordered item list, for the review bar (selection + next-to-review).
const itemNodes = new Map<number, RegisterNode>();
let orderedItems: RegisterNode[] = [];
// assetId -> {node, dim} so plan mode can colour each layer by its sign-off status.
const assetDim = new Map<number, { node: RegisterNode; dim: "2D" | "3D" }>();
let planActive = false;

const STATUS_COLOR: Record<string, string> = {
  approved: "#22C55E",
  needs_work: "#ff4d4d",
  rejected: "#ff4d4d",
  excluded: "#5a626b",
  not_started: "#e0a020",
};
function planColorFor(assetId: number): string {
  const e = assetDim.get(assetId);
  if (!e) return "#8b95a1";
  return STATUS_COLOR[dimState(e.node, e.dim)] ?? "#8b95a1";
}
// Current project's assets - bulk toggles and fly-to must NEVER cross projects.
let currentAssets: Asset[] = [];
let currentAssetMap = new Map<number, Asset>();
let lastLoadedProjectId: number | null = null;
const reviewBar = new ReviewBar();

// item id -> "Mining / Mine Pits / Mine Pit 1" breadcrumb, for the hover tooltip.
const itemPath = new Map<number, string>();

function indexTree(roots: RegisterNode[]): void {
  itemNames.clear();
  itemNodes.clear();
  assetDim.clear();
  itemPath.clear();
  orderedItems = [];
  const walk = (n: RegisterNode, ancestors: string[]) => {
    if (n.layer_name) itemNames.set(n.id, n.layer_name);
    itemNodes.set(n.id, n);
    const path = [...ancestors, n.layer_name ?? "(layer)"];
    itemPath.set(n.id, path.join(" / "));
    if (n.node_type === "item") {
      orderedItems.push(n);
      for (const a of n.assets ?? []) {
        assetDim.set(a.id, { node: n, dim: a.dimension === "2D" ? "2D" : "3D" });
      }
    }
    n.children.forEach((c) => walk(c, path));
  };
  roots.forEach((r) => walk(r, []));
}

function selectAndFly(node: RegisterNode, fly = true): void {
  $selectedItemId.set(node.id);
  $selectedItemName.set(node.layer_name);
  // Clicking data in the viewport selects it (to comment) WITHOUT moving the camera - you
  // can already see it. Flying is only for tree clicks, where you're navigating to it.
  if (fly) layers.flyToAssets(allLocated(node, currentAssetMap).map(toAssetRef));
  document.querySelectorAll(".node-row.selected").forEach((n) => n.classList.remove("selected"));
  const rowEl = document.querySelector(`.node-row[data-id="${node.id}"]`);
  rowEl?.classList.add("selected");
  rowEl?.scrollIntoView({ block: "nearest" });
}

// Click a 3D model / 2D feature on the globe to select its layer; hover shows the path.
function setupMapPicking(): void {
  const handler = new ScreenSpaceEventHandler(viewer.canvas);
  const tip = document.createElement("div");
  tip.className = "pick-tooltip";
  document.body.append(tip);

  handler.setInputAction((e: { position: Cartesian2 }) => {
    if (markup.getTool() !== "none") return; // markup owns clicks while a tool is armed
    const aid = layers.assetIdForPick(viewer.scene.pick(e.position));
    const entry = aid != null ? assetDim.get(aid) : null;
    if (entry) selectAndFly(entry.node, false); // select to comment; don't move the camera
  }, ScreenSpaceEventType.LEFT_CLICK);

  handler.setInputAction((e: { endPosition: Cartesian2 }) => {
    if (markup.getTool() !== "none") {
      tip.style.display = "none";
      return;
    }
    const aid = layers.assetIdForPick(viewer.scene.pick(e.endPosition));
    const entry = aid != null ? assetDim.get(aid) : null;
    if (entry) {
      tip.textContent = `${itemPath.get(entry.node.id) ?? entry.node.layer_name ?? ""}  -  ${entry.dim}`;
      const rect = viewer.canvas.getBoundingClientRect();
      tip.style.left = `${rect.left + e.endPosition.x + 14}px`;
      tip.style.top = `${rect.top + e.endPosition.y + 14}px`;
      tip.style.display = "block";
      viewer.canvas.style.cursor = "pointer";
    } else {
      tip.style.display = "none";
      if (viewer.canvas.style.cursor === "pointer") viewer.canvas.style.cursor = "";
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);
}

// Drive the bottom review bar: the verdict UI for the selected layer.
reviewBar.setHandlers({
  review: async (node, dim, status, notes) => {
    const pid = $activeProjectId.get();
    if (pid == null) return null;
    const d = dim.toUpperCase() as "2D" | "3D";
    const reviewer = $user.get() || null;
    const setOne = async (n: RegisterNode, st: ReviewState, nt: string | null) => {
      const { review } = await api.setReview(pid, n.id, { dimension: dim, status: st, notes: nt, reviewer });
      n.review = review;
      refreshTreeItemReview(n.id);
      // Signing off a dimension marks its open comments/issues done too (they're addressed).
      if (st === "approved") await resolveOpenMarkupsFor(pid, n.id, d);
      return review;
    };
    try {
      // Signing off a GROUP cascades to its sub-layers. If any have open issues, warn first;
      // confirming clears those issues (marks the whole group approved).
      const kids = collectDimDescendants(node, d);
      if (status === "approved" && kids.length) {
        const flagged = kids.filter((k) => ["needs_work", "rejected"].includes(dimState(k, d)));
        if (flagged.length) {
          const ok = window.confirm(
            `${flagged.length} sub-layer(s) under "${node.layer_name}" have open ${d} issues:\n` +
              flagged.slice(0, 8).map((k) => `  • ${k.layer_name}`).join("\n") +
              (flagged.length > 8 ? `\n  …and ${flagged.length - 8} more` : "") +
              `\n\nSign everything off as approved and CLEAR those issues?`,
          );
          if (!ok) return null;
        }
        for (const k of kids) await setOne(k, "approved", null);
        const review = await setOne(node, "approved", notes);
        if (planActive) layers.setPlanColoring(planColorFor);
        void markupPanel?.refresh();
        void reviewBar.refreshComments();
        toast(`${node.layer_name} + ${kids.length} sub-layers ${d} signed off`, "ok");
        return review;
      }

      const review = await setOne(node, status, notes);
      // Keep group sign-off in sync with its children:
      if (status === "approved") {
        // When the last child is signed off, the parent group completes automatically.
        const autoDone: string[] = [];
        for (const anc of ancestorsOf(node)) {
          const kids = collectDimDescendants(anc, d);
          const complete = kids.length
            && kids.every((k) => ["approved", "excluded"].includes(dimState(k, d)))
            && kids.some((k) => dimState(k, d) === "approved");
          if (!complete) break; // a higher ancestor can't be complete if this one isn't
          if (dimState(anc, d) !== "approved") {
            await setOne(anc, "approved", null);
            autoDone.push(anc.layer_name ?? "group");
          }
        }
        if (autoDone.length) toast(`Auto signed off: ${autoDone.join(", ")}`, "ok");
      } else if (status !== "excluded") {
        // Any sub-layer that is no longer signed off - whether flagged (needs_work/rejected)
        // OR un-ticked back to not-started - revokes its signed-off ancestor groups, since
        // the whole group is no longer "all signed off". Flagging downgrades the group to an
        // issue; un-ticking just clears its sign-off. (Excluded children don't block.)
        const flagged = status === "needs_work" || status === "rejected";
        const revoked: string[] = [];
        for (const anc of ancestorsOf(node)) {
          if (dimState(anc, d) === "approved") {
            await setOne(anc, flagged ? "needs_work" : "not_started",
              flagged ? `Sub-layer flagged: ${node.layer_name}` : null);
            revoked.push(anc.layer_name ?? "group");
          }
        }
        if (revoked.length) toast(`Sign-off removed from: ${revoked.join(", ")}`, "warn");
      }
      if (planActive) layers.setPlanColoring(planColorFor); // recolour the plan live
      // Keep the comment log + review bar in sync (sign-off may have resolved issues).
      void markupPanel?.refresh();
      void reviewBar.refreshComments();
      if (status === "approved") toast(`${node.layer_name} ${d} signed off`, "ok");
      else if (status === "needs_work") toast(`${node.layer_name} ${d} flagged`, "warn");
      else if (status === "excluded") toast(`${node.layer_name} ${d} excluded`, "info");
      else toast(`${node.layer_name} ${d} cleared`, "info");
      return review;
    } catch (err) {
      toast(`Review failed: ${(err as Error).message}`, "warn");
      return null;
    }
  },
  comment: (node) => {
    selectAndFly(node);
    markup.setTool("pin");
  },
  selectNode: (node) => selectAndFly(node),
  nextUnreviewed: (fromNode) => {
    const idx = orderedItems.findIndex((n) => n.id === fromNode.id);
    const N = orderedItems.length;
    for (let i = 1; i <= N; i++) {
      const cand = orderedItems[(idx + i) % N];
      if (overallStatus(cand) === "not_started") return cand;
    }
    return null;
  },
});
// When a layer comment is added/resolved in the bar, refresh the markup list + polling sig.
reviewBar.setOnCommentsChanged(() => void markupPanel?.refresh());

// Selecting a layer (tree click, comment, or next) opens the bar for it.
$selectedItemId.subscribe((id) => {
  if (id != null && itemNodes.has(id)) reviewBar.show(itemNodes.get(id)!);
  else reviewBar.hide();
});

// Ancestor group nodes (parent, grandparent...) of a node, via parent_id + the node index.
function ancestorsOf(node: RegisterNode): RegisterNode[] {
  const out: RegisterNode[] = [];
  let pid = node.parent_id;
  while (pid != null) {
    const parent = itemNodes.get(pid);
    if (!parent) break;
    out.push(parent);
    pid = parent.parent_id;
  }
  return out;
}

// Mark a layer's open comments (for one dimension) resolved - used when its dimension is
// signed off, so ticking off a layer also ticks off its issues. Returns how many it closed.
async function resolveOpenMarkupsFor(pid: number, nodeId: number, dim: "2D" | "3D"): Promise<number> {
  try {
    const { comments } = await api.getItemComments(pid, nodeId);
    const open = comments.filter((c) => !c.resolved && c.dimension === dim);
    for (const c of open) await api.patchMarkup(pid, c.id, { resolved: true, actor: $user.get() || null });
    return open.length;
  } catch {
    return 0;
  }
}

// Descendant nodes (children, grandchildren...) that expect the given dimension - the
// sub-layers a group sign-off should cascade to (e.g. each Mine Pit under "Mine Pits").
function collectDimDescendants(node: RegisterNode, dim: "2D" | "3D"): RegisterNode[] {
  const out: RegisterNode[] = [];
  const walk = (n: RegisterNode) => {
    for (const c of n.children ?? []) {
      if (expectedDims(c).includes(dim)) out.push(c);
      walk(c);
    }
  };
  walk(node);
  return out;
}

// All locatable assets on a node (and descendants) - for framing multi-asset layers.
function allLocated(node: RegisterNode, assetMap: Map<number, Asset>): Asset[] {
  const out: Asset[] = [];
  const walk = (n: RegisterNode) => {
    for (const ref of n.assets ?? []) {
      const a = assetMap.get(ref.id);
      if (a && a.lon != null && a.lat != null) out.push(a);
    }
    n.children.forEach(walk);
  };
  walk(node);
  return out;
}

async function reloadProject(projectId: number, flyTo = false): Promise<void> {
  if (!treeEl) return;
  // Switching projects: drop every loaded layer so nothing leaks across projects
  // (visibility, fly-to targets, terrain).
  if (lastLoadedProjectId !== null && lastLoadedProjectId !== projectId) {
    layers.clearAll();
    $selectedItemId.set(null); // don't carry a selection across projects
  }
  lastLoadedProjectId = projectId;

  const [{ tree }, { assets }, unlinked] = await Promise.all([
    api.registerTree(projectId),
    api.listAssets(projectId),
    api.listUnlinked(projectId),
  ]);
  currentAssets = assets;
  const assetMap = new Map<number, Asset>(assets.map((a) => [a.id, a]));
  currentAssetMap = assetMap;
  indexTree(tree);
  void floodPanel?.refresh(); // show/hide the Flood Modelling picker for this project

  renderTree(
    treeEl,
    tree,
    async (assetId, visible) => {
      const a = assetMap.get(assetId);
      if (a) await layers.setVisible(toAssetRef(a), visible);
    },
    (node) => {
      // Clicking a layer name frames all its data (a layer can span many pieces).
      layers.flyToAssets(allLocated(node, assetMap).map(toAssetRef));
    },
    (node) => {
      // 💬 layer comment: fly there and arm the pin tool, pre-attached to the item.
      layers.flyToAssets(allLocated(node, assetMap).map(toAssetRef));
      markup.setTool("pin");
    },
  );
  // Re-show the bar if the selected layer is still in this project.
  const sel = $selectedItemId.get();
  if (sel != null && itemNodes.has(sel)) reviewBar.show(itemNodes.get(sel)!);
  else reviewBar.hide();

  if (unlinkedEl) {
    renderUnlinkedTray(unlinkedEl, unlinked.assets, flattenItems(tree), {
      onToggle: (asset, visible) => void layers.setVisible(toAssetRef(asset), visible),
      onFly: (asset) => layers.flyToAsset(toAssetRef(asset)),
      isVisible: (assetId) => layers.isVisible(assetId),
      onLink: async (assetIds, itemId) => {
        for (const id of assetIds) {
          await api.linkAsset(projectId, id, itemId);
        }
        const name = itemNames.get(itemId) ?? "layer";
        toast(
          assetIds.length > 1
            ? `Linked ${assetIds.length} assets to ${name}`
            : `Linked to ${name}`,
          "ok",
        );
        await reloadProject(projectId); // assets move into the tree under the item
      },
      onAdopt: async (assetId) => {
        const res = await api.adoptAsset(projectId, assetId);
        toast(`Added "${res.name}" to the register`, "ok");
        await reloadProject(projectId);
      },
    });
  }

  // Ground terrain is driven by the Terrain picker in the view bar (it activates the
  // largest-coverage DEM by default and lets you switch between Wyloo / Eliwana / Regional).

  if (flyTo) {
    const located = assets.find((a) => a.lon != null && a.lat != null);
    if (located) flyToLonLat(viewer, located.lon!, located.lat!, 12000);
  }
}

async function loadFirstProject(): Promise<void> {
  if (!treeEl) return;
  try {
    const projects = await api.listProjects();
    if (projects.length === 0) {
      treeEl.innerHTML =
        '<div class="hint">No project yet. Create one and import a Data Register.</div>';
      return;
    }
    // Restore the last-used project, else the most recent.
    const saved = Number(localStorage.getItem("geaar.activeProject"));
    const project =
      projects.find((p) => p.id === saved) ??
      projects.reduce((a, b) => (b.id > a.id ? b : a));
    $activeProjectId.set(project.id);
    await reloadProject(project.id, true);

    // Resume watching any conversions still running from a previous session.
    const { jobs } = await api.listJobs(project.id);
    const active = jobs.filter((j) => j.status === "queued" || j.status === "running");
    if (active.length) jobsWidget?.watch(project.id, active.map((j) => j.id));

    await markupPanel?.refresh(); // draw persisted markups
  } catch (err) {
    treeEl.innerHTML = `<div class="hint err">Failed to load register: ${(err as Error).message}</div>`;
  }
}

// --- near-live sync: pull review + markup changes so viewport and client see each
// other's issues/sign-offs without a manual refresh. Updates the tree rows in place
// (no re-render, expand state kept) and only redraws markup when it actually changed. ---
const POLL_MS = 20000;
let pollingStarted = false;

async function pollOnce(): Promise<void> {
  const pid = $activeProjectId.get();
  if (pid == null || document.hidden) return;
  // 1) Review / sign-off / strikethrough: update only the changed item rows in place.
  try {
    const { reviews } = await api.getReviews(pid);
    for (const [idStr, review] of Object.entries(reviews)) {
      const id = Number(idStr);
      const node = itemNodes.get(id);
      if (!node) continue;
      if (JSON.stringify(node.review) !== JSON.stringify(review)) {
        node.review = review;
        refreshTreeItemReview(id);
        if ($selectedItemId.get() === id) reviewBar.show(node);
      }
    }
  } catch {
    /* transient network blip - try again next tick */
  }
  // 2) Markup: redraw only when the user isn't mid-draw and has no open form/card,
  // so a poll never wipes a stroke in progress or closes an open comment.
  if (markup.getTool() === "none" && !document.querySelector(".markup-form")) {
    try {
      await markupPanel?.refresh();
      // Keep the selected layer's comment thread current too (resolve/edit made elsewhere),
      // but never while the user is mid-edit in the bar.
      if (!document.querySelector(".rb-cmt-editbox")) await reviewBar.refreshComments();
    } catch {
      /* ignore */
    }
  }
}

function startPolling(): void {
  if (pollingStarted) return;
  pollingStarted = true;
  window.setInterval(() => void pollOnce(), POLL_MS);
}

async function checkBackend(): Promise<void> {
  try {
    const h = await api.health();
    $connected.set(true);
    $backendVersion.set(h.version);
    // Client view stays minimal: just the version. Admin sees the CRS too.
    setStatus(isAdmin ? `v${h.version} · CRS ${h.crs_default}` : `v${h.version}`, "ok");
    await loadFirstProject();
    startPolling();
  } catch (err) {
    $connected.set(false);
    setStatus(`backend unreachable: ${(err as Error).message}`, "err");
  }
}

// Remember the sidebar width the reviewer drags to (it can get cramped with icons + pills).
const sidebarEl = document.getElementById("sidebar");
if (sidebarEl) {
  const saved = localStorage.getItem("geaar.sidebarWidth");
  if (saved) sidebarEl.style.width = saved;
  new ResizeObserver(() => {
    if (sidebarEl.style.width) localStorage.setItem("geaar.sidebarWidth", sidebarEl.style.width);
  }).observe(sidebarEl);
}

// Gate the whole app behind login (no-op when auth is disabled), then boot.
setupMapPicking();

void ensureAuth().then((role) => {
  isAdmin = role === "admin";
  applyAdminGating();
  void checkBackend();
});
